/* eslint-disable no-unsafe-optional-chaining */
import { useScreenSize } from '@hooks';
import { Button, Flex, theme, Typography } from '@styles';
import { ReactComponent as Play } from '@svgs/play_circle.svg';
import { arrayOf, bool, func, number, object } from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../Icon';

const VerticalProgressBarWithElements = ({
  currentLessonId,
  elements,
  finalElement,
  handleClick,
  handleFinalElementClick,
  isCurrent,
  isProfileBar,
}) => {
  const [playButtonIndex, setPlayButtonIndex] = useState(null);
  const { isTabletSM } = useScreenSize();

  const { i18n } = useTranslation();

  const onLessonHover = (index) => () => {
    setPlayButtonIndex(index);
  };

  const onLessonHoverOut = () => {
    setPlayButtonIndex(null);
  };

  const getLessonTextColor = (element) => {
    if (isProfileBar) {
      return currentLessonId === element.id
        ? theme.colors.mercury[100]
        : theme.colors.silverChalice[100];
    }

    if (element.is_highlighted_on_map) {
      return theme.colors.mercury[100];
    }

    return element.is_completed
      ? theme.colors.silverChalice[100]
      : theme.colors.mercury[100];
  };

  const isContinueButtonVisible = (element) =>
    isProfileBar && currentLessonId === element.id && !isTabletSM;

  return (
    <Flex flexDirection="column" width="100%">
      {elements?.map((element, index) => (
        <Flex
          key={element?.id}
          borderLeftColor={
            (isProfileBar ? currentLessonId === element.id : isCurrent) &&
            !isProfileBar
              ? theme.colors.schoolBusYellow[100]
              : theme.colors.white[100]
          }
          borderLeftStyle="solid"
          borderLeftWidth={2}
          flexDirection="row"
          justifyContent="flex-start"
          pb={10}
          position="relative"
          pt={10}
        >
          <Button
            border={0}
            height="fit-content"
            ml={20}
            onClick={handleClick(element?.id)}
            onMouseOut={onLessonHoverOut}
            onMouseOver={onLessonHover(index)}
            variant="quaternary"
          >
            <Typography
              color={getLessonTextColor(element)}
              textAlign="left"
              variant="MS-regular-s16-lh25"
            >
              {element?.title}
            </Typography>
          </Button>
          {isContinueButtonVisible(element) && (
            <Button
              border={0}
              height={25}
              onClick={handleClick(element?.id)}
              style={{ overflow: 'hidden' }}
              variant="quaternary"
            >
              <Typography
                color={theme.colors.schoolBusYellow[100]}
                variant="MS-regular-s16-lh25"
              >
                {i18n.t('ProfilePage.Continue')}
              </Typography>
            </Button>
          )}
          <Icon
            display={playButtonIndex !== index && 'none'}
            left={0}
            position="absolute"
            SVG={Play}
            transform="translate(-50%, 0%)"
            zIndex={10}
          />
          <Flex
            backgroundColor={theme.colors.white[100]}
            borderRadius="50%"
            display={
              (
                isProfileBar
                  ? currentLessonId === element.id
                  : element?.is_highlighted_on_map
              )
                ? 'flex'
                : 'none'
            }
            height={10}
            left={0}
            position="absolute"
            transform="translate(-60%, 60%)"
            width={10}
          />
        </Flex>
      ))}
      {finalElement && (
        <Flex
          alignItems="center"
          borderLeftColor={
            (isProfileBar ? currentLessonId === finalElement?.id : isCurrent) &&
            !isProfileBar
              ? theme.colors.schoolBusYellow[100]
              : theme.colors.white[100]
          }
          borderLeftStyle="solid"
          borderLeftWidth={2}
          flexDirection="row"
          justifyContent="flex-start"
          pb={10}
          position="relative"
          pt={10}
        >
          <Button
            border={0}
            disabled={finalElement?.is_disabled}
            height="fit-content"
            ml={20}
            onClick={handleFinalElementClick}
            onMouseOut={onLessonHoverOut}
            onMouseOver={onLessonHover(elements?.length)}
            variant="quaternary"
          >
            <Typography
              color={
                finalElement?.is_disabled
                  ? theme.colors.silverChalice[100]
                  : theme.colors.mercury[100]
              }
              textAlign="left"
              variant="MS-regular-s16-lh25"
            >
              {i18n.t('TagFormat.Test.FinalTest')}
            </Typography>
          </Button>
          {!finalElement?.is_passed && isCurrent && (
            <Button
              border={0}
              height={25}
              onClick={handleFinalElementClick}
              variant="quaternary"
            >
              <Typography
                color={theme.colors.schoolBusYellow[100]}
                variant="MS-regular-s16-lh25"
              >
                {i18n.t('ProfilePage.PassNow')}
              </Typography>
            </Button>
          )}
          <Icon
            display={playButtonIndex !== elements?.length && 'none'}
            left={0}
            position="absolute"
            SVG={Play}
            transform="translate(-50%, 0%)"
            zIndex={10}
          />
          <Flex
            backgroundColor={theme.colors.white[100]}
            borderRadius="50%"
            display={
              elements?.[elements?.length - 1]?.is_highlighted_on_map
                ? 'flex'
                : 'none'
            }
            height={10}
            left={0}
            position="absolute"
            transform="translate(-60%, -5%)"
            width={10}
          />
        </Flex>
      )}
    </Flex>
  );
};

VerticalProgressBarWithElements.propTypes = {
  currentLessonId: number,
  elements: arrayOf(object).isRequired,
  finalElement: object,
  handleClick: func,
  handleFinalElementClick: func,
  isCurrent: bool,
  isProfileBar: bool,
};

VerticalProgressBarWithElements.defaultProps = {
  currentLessonId: 0,
  finalElement: null,
  handleClick: () => {},
  handleFinalElementClick: () => {},
  isCurrent: false,
  isProfileBar: false,
};

export default VerticalProgressBarWithElements;
